import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Alert, Paper, Divider } from '@mui/material';
import { useTranslation } from "react-i18next";

export default function OAuthCallback() {
    const { t } = useTranslation();
    const [status, setStatus] = useState('processing');
    const [message, setMessage] = useState(t("OAUTH.PROCESSING"));
    const [debugInfo, setDebugInfo] = useState({});
    const showDebugInfo =
        process.env.NODE_ENV !== 'production' &&
        process.env.REACT_APP_DEBUG_OAUTH === 'true';

    useEffect(() => {
        // Extract query parameters from URL
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        // Debug information
        const debug = {
            url: window.location.href,
            params: Object.fromEntries(urlParams.entries()),
            hasOpener: !!window.opener,
            timestamp: new Date().toISOString()
        };
        setDebugInfo(debug);

        // Handle the callback
        if (error) {
            // Error case
            const errorMsg = t("OAUTH.ERROR_PREFIX", { message: errorDescription || error });
            setStatus('error');
            setMessage(errorMsg);

            // Send detailed error message to parent window
            if (window.opener) {
                try {
                    window.opener.postMessage({
                        type: 'oauth_error',
                        error: errorDescription || error,
                        errorDetails: debug,
                        timestamp: new Date().toISOString()
                    }, '*');

                    // Close window after a short delay
                    setTimeout(() => window.close(), 3000);
                } catch (e) {
                    setMessage(t("OAUTH.PARENT_COMM_ERROR", { message: e.message }));
                }
            } else {
                setMessage(`${errorMsg}\n\n${t("OAUTH.PARENT_NOT_FOUND")}`);
            }
        } else if (code) {
            // Success case - we have an authorization code
            setStatus('success');
            setMessage(t("OAUTH.CODE_RECEIVED"));

            // Send success message to parent window with the code
            if (window.opener) {
                try {
                    window.opener.postMessage({
                        type: 'oauth_success',
                        code: code,
                        message: t("OAUTH.CONNECTED"),
                        timestamp: new Date().toISOString()
                    }, '*');

                    setMessage(t("OAUTH.SUCCESS_CLOSING"));

                    // Close window after a short delay
                    setTimeout(() => window.close(), 2000);
                } catch (e) {
                    setMessage(t("OAUTH.PARENT_COMM_ERROR", { message: e.message }));
                    setStatus('error');
                }
            } else {
                setMessage(t("OAUTH.SUCCESS_PARENT_NOT_FOUND"));
            }
        } else {
            // No code or error
            setStatus('error');
            setMessage(t("OAUTH.MISSING_PARAMS"));

            // Send error to parent window
            if (window.opener) {
                try {
                    window.opener.postMessage({
                        type: 'oauth_error',
                        error: t("OAUTH.MISSING_PARAMS_SHORT"),
                        errorDetails: debug,
                        timestamp: new Date().toISOString()
                    }, '*');

                    // Close window after a delay
                    setTimeout(() => window.close(), 3000);
                } catch (e) {
                }
            }
        }
    }, [t]);

    return (
        <Box
            sx={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                p: 3,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    borderRadius: 3,
                    backdropFilter: 'blur(10px)',
                    maxWidth: 500,
                    width: '100%',
                }}
            >
                <Box sx={{ mb: 3 }}>
                    {status === 'processing' && <CircularProgress size={40} sx={{ mb: 2 }} />}
                    <Typography variant="h5" gutterBottom>
                        {status === 'success' ? t("OAUTH.SUCCESS_TITLE") :
                            status === 'error' ? t("OAUTH.ERROR_TITLE") :
                                t("OAUTH.AUTHORIZING_TITLE")}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                        {message}
                    </Typography>
                </Box>

                <Alert
                    severity={status === 'success' ? "success" : status === 'error' ? "error" : "info"}
                    sx={{ mt: 2 }}
                    variant="filled"
                >
                    {status === 'success' ? t("OAUTH.AUTO_CLOSE") :
                        status === 'error' ? t("OAUTH.ERROR_DETAILS") :
                            t("OAUTH.WAIT_PROCESSING")}
                </Alert>

                {showDebugInfo && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="caption" component="div" sx={{ textAlign: 'left', mb: 1, fontWeight: 'bold' }}>
                            {t("OAUTH.DEBUG_INFO")}
                        </Typography>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                bgcolor: 'rgba(0,0,0,0.03)',
                                maxHeight: '150px',
                                overflow: 'auto',
                                fontSize: '0.7rem'
                            }}
                        >
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                {JSON.stringify(debugInfo, null, 2)}
                            </pre>
                        </Paper>
                    </>
                )}
            </Paper>
        </Box>
    );
} 
